import { useState } from "react";
import { Controller } from "react-hook-form";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../components/ui/command";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../../components/ui/popover";
import { Button } from "../../../../components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import { cn } from "../../../../helpers/cn";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";

export function CommoditySelect({
  control,
  errors,
  commodityOptions,
  updateCarriers,
  onlyAccelerant,
}) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCommodity, setPendingCommodity] = useState(null);

  const handleConfirmAccelerant = (fieldOnChange) => {
    if (!pendingCommodity) return;

    // Set the selected commodity
    fieldOnChange(pendingCommodity.value);

    updateCarriers();

    setOpen(false);
    setConfirmOpen(false);
    setPendingCommodity(null);
  };

  return (
    <>
      <Controller
        control={control}
        name="commodityKey"
        render={({ field }) => {
          const selected = commodityOptions.find((c) => c.value === field.value);

          return (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className={cn(
                    "w-full justify-between",
                    errors.commodityKey && "border-red-500 focus:ring-red-500"
                  )}
                >
                  {selected ? selected.label : "Select commodity"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0">
                <Command>
                  <CommandInput placeholder="Search commodities..." />
                  <CommandList
                    className="max-h-[40vh] overflow-y-auto overscroll-contain"
                    onWheel={(e) => {
                      e.stopPropagation();
                      const el = e.currentTarget;
                      el.scrollBy({ top: e.deltaY, behavior: "auto" });
                    }}
                  >
                    <CommandEmpty>No commodities found.</CommandEmpty>
                    <CommandGroup>
                      {commodityOptions.map((opt) => (
                        <CommandItem
                          key={opt.value}
                          value={opt.label}
                          onSelect={() => {
                            if (opt.forAccelerantOnly && !onlyAccelerant) {
                              // show confirmation dialog instead of immediately changing
                              setPendingCommodity(opt);
                              setConfirmOpen(true);
                            } else {
                              field.onChange(opt.value);
                              setOpen(false);
                            }
                          }}
                        >
                          {opt.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>

              {/* Accelerant confirmation dialog */}
              <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Accelerant-only commodity
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      With this commodity you will only be able to run this quote
                      with Accelerant.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel
                      onClick={() => {
                        setPendingCommodity(null);
                        // keep popover open so they can pick another option
                      }}
                    >
                      Go back
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleConfirmAccelerant(field.onChange)}
                    >
                      Confirm
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </Popover>
          );
        }}
      />
    </>
  );
}
